import React, {
  createContext,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { IProjectSchema, ProjectService } from '@/api/project';
import { Section } from '@/components/Section';
import { useAccess } from '@/utils/hooks/useAccess';
import { useNavigate, useParams } from 'react-router-dom';
import { TestCaseModule } from '@/components/module/TestCaseModule';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { IModuleSchema } from '@/api/module';
import { Loading } from '@/components/Loading';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { Provider } from 'react-redux';
import moduleStore from '@/states/moduleStore';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { BiSolidShow } from 'react-icons/bi';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

type IListModuleContext = {
  isViewer?: boolean;
  project?: IProjectSchema;
};

interface CustomActionCellProps {
  data: IModuleSchema;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({ data }) => {
  return <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />;
};

export const ListModuleContext = createContext<IListModuleContext>({});

export const ListArchiveModule: React.FC = () => {
  return (
    <Provider store={moduleStore}>
      <ListArchiveModuleContent />
    </Provider>
  );
};

const ListArchiveModuleContent: React.FC = () => {
  const { id } = useParams();
  const isViewer = useAccess(id!);
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);
  const contextValue = useMemo(() => ({ isViewer }), [isViewer]);

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);
  const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(
    undefined,
  );

  // gunain useMemo agar tidak mengulang ini filternya
  const filterSpec: IFilterSpec[] = useMemo(
    () => [
      { name: 'case_code', label: 'Case Code', type: 'textarea' },
      { name: 'title', label: 'Title', type: 'text' },
    ],
    [],
  );

  const renderCustomActionCell = useCallback(
    (data: any) => <CustomActionCell data={data as IModuleSchema} />,
    [],
  );

  const spec = useCustomSpec('/api/crud/test-case', {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'title', label: 'Title' },
      { key: 'total_step', label: 'Step' },
    ],
  });

  if (isProjectFetching) {
    return <Loading />;
  }

  const handleSelectModule = (module: IModuleSchema) => {
    setSelectedModuleId(module.id);
    ref.current?.filter({
      module_id: module.id,
    });
    ref.current?.show();
  };

  const backNavigate = () => navigate(-1);

  return (
    <Section
      title="Archived Epic and Test Cases"
      actions={
        !isViewer ? (
          <div className="flex flex-row gap-3">
            <OutlineButton onClick={backNavigate}>Back</OutlineButton>
          </div>
        ) : (
          <></>
        )
      }
    >
      <ListModuleContext.Provider value={contextValue}>
        <div className="flex flex-row min-h-screen">
          <div className="w-[30%]">
            <TestCaseModule
              isViewer={isViewer}
              project={projectData!}
              onSelectedModule={handleSelectModule}
              isArchive={true}
            />
          </div>

          <div className="w-[70%]">
            {!selectedModuleId && (
              <div className="py-6 text-center">
                Select a Epic or User Story
              </div>
            )}

            <ExtendedTableLowCode
              ref={ref}
              hidden={!selectedModuleId}
              spec={spec}
              defaultFilter={{
                module_id: selectedModuleId ?? undefined,
                project_id: id,
                is_archive: 'true',
              }}
              defaultSort="case_code"
              defaultSortOrder="asc"
              customButtonCreate={HideCustomCreateButton}
              customActionCell={renderCustomActionCell}
              filterSpec={filterSpec}
              showFilter
            />
          </div>
        </div>
      </ListModuleContext.Provider>
    </Section>
  );
};
